/**
 * PlanningSessionSwitcher — header dropdown for the Planning Agent rail.
 * Lists sessions (== `content_brief_plans` rows) for the active Piece, and
 * supports new / rename / archive / delete, mirroring `ChatThreadSwitcher`.
 */
import { useState } from "react";
import { Check, ChevronDown, Pencil, Plus, Archive, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  archivePlanningSession,
  createPlanningSession,
  deletePlanningSession,
  planningSessionLabel,
  renamePlanningSession,
  type PlanningSession,
} from "@/lib/planningSessions";

type Props = {
  workspaceId: string;
  briefId: string;
  sessions: PlanningSession[];
  activePlanId: string | null;
  onSelect: (planId: string) => void;
  onChanged: () => Promise<void> | void;
  onDeleted?: (deletedPlanId: string) => void;
  disabled?: boolean;
};

export const PlanningSessionSwitcher = ({
  workspaceId,
  briefId,
  sessions,
  activePlanId,
  onSelect,
  onChanged,
  onDeleted,
  disabled,
}: Props) => {
  const [creating, setCreating] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const active = sessions.find((s) => s.id === activePlanId) ?? null;
  const visible = sessions.filter((s) => showArchived || !s.archived_at);

  const handleNew = async () => {
    setCreating(true);
    try {
      const session = await createPlanningSession(workspaceId, briefId);
      await onChanged();
      onSelect(session.id);
      toast.success("New Planning session started");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not create session");
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = async (s: PlanningSession) => {
    try {
      await archivePlanningSession(s.id, !s.archived_at);
      toast.success(s.archived_at ? "Session unarchived" : "Session archived");
      await onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not archive");
    }
  };

  const handleDelete = async (s: PlanningSession) => {
    const label = planningSessionLabel(s);
    const ok = window.confirm(`Delete "${label}"? This can't be undone.`);
    if (!ok) return;
    try {
      await deletePlanningSession(s.id);
      toast.success("Session deleted");
      if (onDeleted) onDeleted(s.id);
      else await onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not delete");
    }
  };

  const startRename = () => {
    if (!active) return;
    setRenameValue(active.title ?? "");
    setRenameOpen(true);
  };

  const submitRename = async () => {
    if (!active) return;
    const next = renameValue.trim();
    if (!next) {
      toast.error("Title can't be empty");
      return;
    }
    setRenaming(true);
    try {
      await renamePlanningSession(active.id, next);
      await onChanged();
      setRenameOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not rename");
    } finally {
      setRenaming(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            className="h-7 max-w-[220px] gap-1 px-2 text-xs"
            title="Switch Planning session"
          >
            <span className="truncate">
              {active ? planningSessionLabel(active) : "New session"}
            </span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>Planning sessions</span>
            <button
              type="button"
              onClick={() => setShowArchived((s) => !s)}
              className="text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
            >
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {visible.length === 0 ? (
            <div className="px-2 py-3 text-xs text-muted-foreground">
              No sessions yet — start one below.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {visible.map((s) => {
                const isActive = s.id === activePlanId;
                return (
                  <div
                    key={s.id}
                    className={cn(
                      "group/session flex items-center gap-1 px-1.5 py-0.5",
                      isActive ? "bg-accent/50" : "hover:bg-accent/30",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(s.id)}
                      className="flex-1 truncate rounded px-1.5 py-1 text-left text-xs"
                    >
                      <div className="flex items-center gap-1.5">
                        {isActive ? (
                          <Check className="h-3 w-3 shrink-0 text-primary" />
                        ) : (
                          <span className="inline-block h-1.5 w-1.5 shrink-0" />
                        )}
                        <span className={cn("truncate", s.archived_at && "italic opacity-60")}>
                          {planningSessionLabel(s)}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleArchive(s)}
                      className="rounded p-1 text-muted-foreground opacity-0 hover:bg-background hover:text-foreground group-hover/session:opacity-100"
                      title={s.archived_at ? "Unarchive" : "Archive"}
                    >
                      {s.archived_at ? (
                        <RotateCcw className="h-3 w-3" />
                      ) : (
                        <Archive className="h-3 w-3" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(s)}
                      className="rounded p-1 text-muted-foreground opacity-0 hover:bg-background hover:text-destructive group-hover/session:opacity-100"
                      title="Delete session"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={creating} onSelect={(e) => { e.preventDefault(); void handleNew(); }}>
            {creating ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-2 h-3.5 w-3.5" />}
            New session
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!active} onSelect={(e) => { e.preventDefault(); startRename(); }}>
            <Pencil className="mr-2 h-3.5 w-3.5" />
            Rename current
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Rename Planning session</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Session title"
            maxLength={200}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)} disabled={renaming}>
              Cancel
            </Button>
            <Button onClick={submitRename} disabled={renaming}>
              {renaming ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
